import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FL140, l204Api } from '../../services/l204Api';
import { useL204Mode } from './hooks/useL204Mode';
import { useL204Record } from './hooks/useL204Record';
import { useL204Navigation } from './hooks/useL204Navigation';
import { useL204Permissions } from './hooks/useL204Permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import ActionBar from './components/ActionBar';
import { isFieldDisabledFn, isApprovalLocked } from './helpers';
import './L204Form.css';

const L204Form: React.FC = () => {
  const navigate = useNavigate();
  const [quickDocNo, setQuickDocNo] = useState('');
  const [quickPageNo, setQuickPageNo] = useState('');

  // Hooks
  const perms = useL204Permissions();
  const { mode, setMode, isEditing } = useL204Mode();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = useL204Record();

  const loadRecordDirect = useCallback(async (docNo: string, pageNo: number) => {
    await loadRecord(docNo, pageNo);
  }, [loadRecord]);

  const { allKeys, currentIdx, setCurrentIdx, loadList, navTo, findIdx } =
    useL204Navigation(loadRecordDirect);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const keys = await loadList();
        if (keys.length > 0) {
          setCurrentIdx(0);
          await loadRecord(keys[0].docNo, keys[0].pageNo);
        }
      } catch {
        console.error('Failed to load L204 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quick Search (CTNQK)
  const handleQuickSearch = useCallback(async () => {
    const docNo = quickDocNo.trim();
    const pageNo = parseFloat(quickPageNo) || 0;
    if (!docNo) {
      message.warning('請輸入文件編號');
      return;
    }
    try {
      const data = await l204Api.get(docNo, pageNo);
      if (data) {
        applyResult(data);
        // Refresh list and find index
        const keys = await loadList();
        const idx = keys.findIndex(k => k.docNo === data.fl140_01 && k.pageNo === data.fl140_02);
        if (idx >= 0) setCurrentIdx(idx);
      }
    } catch {
      message.warning('找不到此記錄');
    }
  }, [quickDocNo, quickPageNo, applyResult, loadList, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FL140) => {
        if (mode === 'add') {
          const keys = await loadList();
          const newIdx = keys.findIndex(
            k => k.docNo === result.fl140_01 && k.pageNo === result.fl140_02
          );
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        clearFieldErrors();
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearFieldErrors]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      // BR-F01: check approval lock
      const parentStatus = record.parent_doc?.fl040_80;
      if (isApprovalLocked(parentStatus)) {
        message.warning('文件已核准，無法修改');
        return;
      }
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    // BR-F01: check approval lock
    const parentStatus = record.parent_doc?.fl040_80;
    if (isApprovalLocked(parentStatus)) {
      message.warning('文件已核准，無法刪除');
      return;
    }
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除文件 ${record.fl140_01} 頁次 ${record.fl140_02}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const keys = await loadList();
          if (keys.length > 0) {
            const newIdx = Math.min(currentIdx, keys.length - 1);
            setCurrentIdx(newIdx);
            await loadRecord(keys[newIdx].docNo, keys[newIdx].pageNo);
          } else {
            resetForAdd();
          }
          setMode('browse');
        }
      },
    });
  }, [record, isEditing, currentIdx, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  // Field disabled check
  const isFieldDisabled = useCallback((key: string): boolean => {
    return isFieldDisabledFn(key, isEditing, mode);
  }, [isEditing, mode]);

  return (
    <PermissionGuard module={Modules.L204} action="query">
      <div className="l204-form">
        {loading && <div className="loading-overlay">載入中...</div>}

        <div className="page-wrapper">
          {/* Page Title */}
          <div className="page-title-bar">
            <div className="page-title-left">
              <div className="breadcrumb-inline">
                <a href="/erp">ERP</a>
                <span>/</span>
                <span className="current">ISO文件內頁建檔</span>
              </div>
              <span className="title-separator">|</span>
              <h1>L204 ISO文件內頁建檔</h1>
              {record && (
                <span className="badge badge-default">
                  {record.fl140_01} / 頁 {record.fl140_02}
                </span>
              )}
              {mode === 'add' && <span className="badge badge-warning">新增模式</span>}
              {mode === 'edit' && <span className="badge badge-warning">編輯模式</span>}
            </div>
            <div className="page-title-right">
              <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                {allKeys.length > 0 ? `${currentIdx + 1} / ${allKeys.length}` : '--'}
              </span>
            </div>
          </div>

          {/* Quick Search Bar (CTNQK) */}
          <div className="search-bar">
            <div className="field-group">
              <label>文件編號</label>
              <input
                type="text"
                maxLength={10}
                style={{ width: 120 }}
                disabled={isEditing}
                value={quickDocNo}
                onChange={e => setQuickDocNo(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleQuickSearch(); }}
              />
            </div>
            <div className="field-group">
              <label>頁次</label>
              <input
                type="number"
                step="0.5"
                style={{ width: 80 }}
                disabled={isEditing}
                value={quickPageNo}
                onChange={e => setQuickPageNo(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleQuickSearch(); }}
              />
            </div>
            <button
              className="btn btn-sm"
              disabled={isEditing}
              onClick={handleQuickSearch}
              style={{ color: '#1890ff' }}
            >
              快速查詢
            </button>
          </div>

          {/* Tab Content (single tab for this module) */}
          <div className="tabs">
            <div className="tab-nav">
              <button className="tab-nav-item active">文件內頁</button>
            </div>
            <div className="tab-pane active">
              <Tab1BasicInfo
                formData={formData}
                isEditing={isEditing}
                mode={mode}
                isFieldDisabled={isFieldDisabled}
                getField={getField}
                setField={setField}
                getFieldError={getFieldError}
              />
            </div>
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allKeysLength={allKeys.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onNavTo={(idx) => navTo(idx, isEditing)}
          onExit={() => navigate('/erp')}
        />
      </div>
    </PermissionGuard>
  );
};

export default L204Form;
